import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations
const translations = {
  nl: {
    // Navigation
    nav: {
      home: 'Home',
      menu: 'Menu',
      gallery: 'Galerij',
      reviews: 'Reviews',
      contact: 'Contact',
      orderNow: 'Bestel Nu'
    },
    // Hero
    hero: {
      subtitle: 'Bakkerij & Eethuis',
      title: 'Vers Brood, Shoarma & Turkse Pizza',
      description: 'Welkom bij AK-AL! Geniet van ons dagelijks vers gebakken brood, heerlijke shoarma, döner, kapsalon en authentieke Turkse pizza\'s.',
      cta: 'Bekijk Menu',
      halal: '100% HALAL',
      openNow: 'Nu Open',
      delivery: 'Bezorging via Thuisbezorgd'
    },
    // Features
    features: {
      title: 'Waarom AK-AL?',
      fresh: {
        title: 'Vers Gebakken',
        desc: 'Dagelijks vers brood uit onze eigen bakkerij'
      },
      halal: {
        title: '100% Halal',
        desc: 'Gecertificeerd halal vlees en producten'
      },
      quality: {
        title: 'Topkwaliteit',
        desc: 'Alleen de beste ingrediënten'
      },
      family: {
        title: 'Familiebedrijf',
        desc: 'Met liefde bereid sinds jaren'
      }
    },
    // Menu
    menu: {
      title: 'Ons Menu',
      subtitle: 'Ontdek onze heerlijke gerechten',
      viewAll: 'Bekijk Volledig Menu',
      categories: {
        broodjes: 'Broodjes',
        turksePizza: 'Turkse Pizza\'s',
        durum: 'Dürüm\'s',
        kapsalon: 'Kapsalon',
        schotels: 'Schotels',
        bakkerij: 'Bakkerij',
        dranken: 'Dranken'
      }
    },
    // Reviews
    reviews: {
      title: 'Wat Onze Klanten Zeggen',
      subtitle: '600+ Tevreden Klanten',
      rating: 'Beoordeling'
    },
    // Gallery
    gallery: {
      title: 'Onze Sfeer',
      subtitle: 'Een kijkje in ons restaurant'
    },
    // Contact
    contact: {
      title: 'Contact & Locatie',
      subtitle: 'Kom langs of neem contact op',
      address: 'Adres',
      phone: 'Telefoon',
      hours: 'Openingstijden',
      everyday: 'Elke dag',
      directions: 'Routebeschrijving',
      callUs: 'Bel Ons',
      whatsapp: 'WhatsApp'
    },
    // Footer
    footer: {
      rights: 'Alle rechten voorbehouden',
      madeWith: 'Gemaakt met',
      in: 'in Alphen aan den Rijn'
    }
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      menu: 'Menu',
      gallery: 'Gallery',
      reviews: 'Reviews',
      contact: 'Contact',
      orderNow: 'Order Now'
    },
    // Hero
    hero: {
      subtitle: 'Bakery & Restaurant',
      title: 'Fresh Bread, Shoarma & Turkish Pizza',
      description: 'Welcome to AK-AL! Enjoy our daily freshly baked bread, delicious shoarma, döner, kapsalon and authentic Turkish pizzas.',
      cta: 'View Menu',
      halal: '100% HALAL',
      openNow: 'Open Now',
      delivery: 'Delivery via Thuisbezorgd'
    },
    // Features
    features: {
      title: 'Why AK-AL?',
      fresh: {
        title: 'Freshly Baked',
        desc: 'Daily fresh bread from our own bakery'
      },
      halal: {
        title: '100% Halal',
        desc: 'Certified halal meat and products'
      },
      quality: {
        title: 'Top Quality',
        desc: 'Only the best ingredients'
      },
      family: {
        title: 'Family Business',
        desc: 'Made with love for years'
      }
    },
    // Menu
    menu: {
      title: 'Our Menu',
      subtitle: 'Discover our delicious dishes',
      viewAll: 'View Full Menu',
      categories: {
        broodjes: 'Sandwiches',
        turksePizza: 'Turkish Pizzas',
        durum: 'Dürüm Wraps',
        kapsalon: 'Kapsalon',
        schotels: 'Plates',
        bakkerij: 'Bakery',
        dranken: 'Drinks'
      }
    },
    // Reviews
    reviews: {
      title: 'What Our Customers Say',
      subtitle: '600+ Happy Customers',
      rating: 'Rating'
    },
    // Gallery
    gallery: {
      title: 'Our Atmosphere',
      subtitle: 'A glimpse into our restaurant'
    },
    // Contact
    contact: {
      title: 'Contact & Location',
      subtitle: 'Visit us or get in touch',
      address: 'Address',
      phone: 'Phone',
      hours: 'Opening Hours',
      everyday: 'Every day',
      directions: 'Get Directions',
      callUs: 'Call Us',
      whatsapp: 'WhatsApp'
    },
    // Footer
    footer: {
      rights: 'All rights reserved',
      madeWith: 'Made with',
      in: 'in Alphen aan den Rijn'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('nl');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'nl' ? 'en' : 'nl');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
